import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { message, Tag } from "antd";
import {
    EditOutlined,
    DeleteOutlined,
    HistoryOutlined
} from "@ant-design/icons";
import usePost from '../../../../services/usePost';
import SOURCES from '../../../../utils/constants';
import { capitalizeFirstLetter, formatDate } from '../../../../utils/functions';
import { MSG_COUPONS } from '../../../../utils/message';
import CommonTable from '../../../global/table';
import CommonDialog from '../../../global/dialog';
import NoRecordFound from '../../../global/noRecordFound';
import EnhancedAddCoupon from './enhanced-add-coupon';
import { useGenericQuery } from '../../../../queries/use-generic-query';
import { QUERY_KEYS } from '../../../../lib/query-constants';
import { useGenericMutation } from '../../../../queries/use-generic-mutation';
import { useQueryClient } from '@tanstack/react-query';
// import TableLoader from '../../common/tableLoader'; // Assuming same path as couponsList relative to admin
import CommonCardView from '../../../global/cardView';
import EnhancedCouponGridView from './enhanced-coupon-grid-view';
import { useHistory, useLocation } from "react-router-dom"
import CommonPagination from '../../../global/pagination';
const EnhancedCouponsList = ({
    coupons,
    setShowDetail,
    setCouponId,
    sort,
    setSort,
    editCoupon,
    setEditCoupon,
    keyParams,
    viewType
}) => {
    const { DISCOUNT_FIXED } = SOURCES;
    const [toggleModal, setToggleModal] = useState({
        show: false,
        type: "",
    });


    const [deleteId, setDeleteId] = useState(null);
    const history = useHistory()

    // mutations
    const queryClient = useQueryClient()
    const deleteCouponMutation = useGenericMutation({
        endpoint: "coupon",
        method: "DELETE",
        onError: (err) => {
            console.error(err)
            message.error(err.response.error || "Error deleting coupon.")
        },
        onSuccess: () => {
            setToggleModal({ show: false, type: "" });
            setDeleteId(null);
            message.success("Coupon deleted successfully.")
            queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.coupons.lists(keyParams),
                type: "active",
                exact: true
            })
        },
    })

    const addCouponMutation = useGenericMutation({
        endpoint: "coupon",
        method: "POST",
        onError: (err) => {
            console.log(err)
            message.error(err.response.data.data.error || "Error creating coupon.")
        },
        onSuccess: () => {
            setEditCoupon({
                show: false,
                id: null
            })
            message.success("Coupon added successfully.")
            queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.coupons.lists(keyParams),
                type: "active",
                exact: true
            })
        },
    })

    const updateCouponMutation = useGenericMutation({
        endpoint: "coupon",
        method: "PUT",
        onError: (err) => {
            console.log(err)
            message.error(err.response.data.data.error || "Error creating coupon.")
        },
        onSuccess: () => {
            setEditCoupon({
                show: false,
                id: null
            })
            message.success("Coupon updated successfully.")
            queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.coupons.lists(keyParams),
                type: "active",
                exact: true
            })
            queryClient.removeQueries({
                queryKey: QUERY_KEYS.coupon.all
            })
        },
    })


    // useEffect(() => {
    //     if (removeSuccess) {
    //         setToggleModal({ show: false, type: "" });
    //         setDeleteId(null);
    //         getCoupons();
    //     }
    // }, [removeSuccess]);

    const handleEdit = async (id) => {
        // if (getCoupon && setId && setShowForm) {
        //     await getCoupon([{ key: "id", val: id }]);
        //     setId(id);
        //     setShowForm(true);
        // }
        if (id) {
            setEditCoupon({
                show: true,
                isEditable: true,
                id: id
            })
        }
    };

    const handleDetails = async (id) => {
        setCouponId(id);
        setShowDetail(true);
    };

    const removeCoupon = async () => {
        try {
            const postData = {
                id: deleteId,
            };
            await deleteCouponMutation.mutateAsync(postData)
        } catch (error) {
            message.error("Failed to delete coupon");
        }
    };

    const isCouponExpired = (expiryDate) => {
        const today = new Date();

        // Clean "Jul 12th 28" → "Jul 12 2028"
        const cleanedDate = expiryDate
            .replace(/(\d+)(st|nd|rd|th)/, "$1") // remove 'th'
            .replace(/\b(\d{2})\b$/, "20$1");   // convert 28 → 2028

        const expiry = new Date(cleanedDate);

        console.log("Expiry Date:", expiry, "Today:", today);

        if (isNaN(expiry.getTime())) return false; // safety check

        return expiry < today;
    };

    // Columns Configuration
    const columns = [
        {
            title: "Coupon Code",
            dataIndex: "code",
            key: "code",
            sortType: "code",
            render: (text, record) => (
                <a onClick={() => handleEdit(record.id)} className="text-primary cursor-pointer hover:underline">
                    {text}
                </a>
            ),
        },
        {
            title: "Type",
            dataIndex: "couponType",
            key: "couponType",
            sortType: "couponType",
        },
        {
            title: "Amount",
            dataIndex: "amount",
            key: "amount",
            sortType: "amount",
        },
        {
            title: "Expiry Date",
            dataIndex: "expiryDate",
            key: "expiryDate",
            sortType: "expiryDate",
        },
        {
            title: "Usage",
            dataIndex: "usage",
            key: "usage",
            sortType: "usage",
        },
        {
            title: "Total Usage Allowed",
            dataIndex: "usageAllowed",
            key: "usageAllowed",
        },
        {
            title: "Expired",
            dataIndex: "isExpired",
            key: "isExpired",
            render: (_, record) => (
                <Tag color={isCouponExpired(record.expiryDate) ? "red" : "green"}>{isCouponExpired(record.expiryDate) ? "Yes" : "No"}</Tag>
            )
        },
        {
            title: "Usage History",
            key: "usageHistory",
            render: (_, record) => (
                <a onClick={() => history.push(`/admin/coupons/usage-history?id=${record.id}`)} className="text-primary cursor-pointer hover:underline">
                    Show History
                </a>
            ),
        }
    ];

    // Data Mapping
    const data = Array.isArray(coupons) && coupons.length > 0
        ? coupons.map((res) => ({
            ...res,
            key: res._id,
            id: res._id,
            code: res.code,
            couponType: capitalizeFirstLetter(res.couponType),
            amount: res.couponType === DISCOUNT_FIXED ? res.amount : `${res.amount}%`,
            expiryDate: res.expiryDate && formatDate(res.expiryDate),
            usage: res.usage,
            usageAllowed: res.usageAllowed === 0 ? 'No Limit' : res.usageAllowed,
        }))
        : [];

    // Actions Configuration
    const actions = [
        {
            label: "Edit",
            icon: <EditOutlined />,
            onClick: (record) => handleEdit(record.id),
            className: "!text-primary !border-primary hover:!border-primary hover:!bg-primary hover:!text-white",
        },
        {
            label: "Delete",
            icon: <DeleteOutlined />,
            condition: (record) => record.usage === 0,
            onClick: (record) => {
                setDeleteId(record.id);
                setToggleModal({ show: true, type: "delete" });
            },
            className: "!text-red-500 !border-red-500 hover:!border-red-500 hover:!bg-red-500 hover:!text-white",
        }
    ];

    return (
        <>
            {data.length > 0 ? (
                <>
                    {viewType === "LIST" ? (
                        <CommonTable
                            columns={columns}
                            data={data}
                            showActions={true}
                            actions={actions}
                            sort={sort}
                            setSort={setSort}
                        />
                    ) : (
                        <CommonCardView>
                            {data.map((res) => (
                                <EnhancedCouponGridView
                                    key={res.id}
                                    record={res}
                                    actions={actions}
                                    onCardClick={(record) => handleDetails(record.id || record)}
                                    isCouponExpired={isCouponExpired}
                                />
                            ))}
                        </CommonCardView>
                    )}
                </>
            ) : (
                <NoRecordFound message="No coupons found." />
            )}

            {toggleModal.show && (
                <CommonDialog
                    title="Delete Coupon"
                    description="Are you sure you want to delete this Coupon?"
                    open={toggleModal.show}
                    setOpen={() => setToggleModal({ show: false, type: "" })}
                    onOk={removeCoupon}
                    onCancel={() => setToggleModal({ show: false, type: "" })}
                    isConfirmButtonLoading={deleteCouponMutation.isPending}
                />
            )}

            {
                editCoupon.show && (
                    <EnhancedAddCoupon
                        showForm={editCoupon.show}
                        setShowForm={() => setEditCoupon({ show: false, isEditable: false, id: null })}
                        editCoupon={editCoupon}
                        addCouponMutation={addCouponMutation}
                        updateCouponMutation={updateCouponMutation}
                    />
                )
            }
        </>
    );
};

export default EnhancedCouponsList;